import { COUNTRY_ENUM } from '@partipost-cm-new/app-preview/src/Constants/CountryConfig'
import { PLATFORM_CONTENT_TYPE_ENUM, PLATFORM_ENUM } from './constants'
import { api, PartipostApiResponse } from './index'
import { PartiposterProfile } from 'state/submissions'
import { MAX_PAGE_SIZE } from 'state/review'
import { ProfileToTagGrouped } from 'state/partiposter'
import { PLATFORM_CONTENT } from 'components/Ambassadors/constants'

export interface UpdateAmbassadorFavoriteRequest {
    partiposter_uuid: string
    brand_uuid: string
    favorited: boolean
}

export interface UpdateAmbassadorFavoriteResponse {
    data: {
        success: boolean
    }
}

export interface BrandResponse {
    uuid: string
    name: string
    country: string
    industry: string
}

export interface TeamResponse {
    uuid: string
    name: string
}

export interface AmbassadorBrand {
    uuid: string
    name: string
    country: COUNTRY_ENUM
    team_name: string
    ambassador_count: number
}

export interface GetAmbassadorBrandsRequest {
    name?: string
    team_uuid?: string
}

export interface GetAmbassadorBrandsResponse {
    count: number
    results: AmbassadorBrand[]
}

export const getAmbassadorBrands = (params: GetAmbassadorBrandsRequest) => {
    const searchParams = new URLSearchParams()

    if (params.name) {
        searchParams.set('keyword', params.name)
    }

    if (params.team_uuid) {
        searchParams.set('team_uuid', params.team_uuid)
    }
    return api.get<PartipostApiResponse<GetAmbassadorBrandsResponse>>('/ambassadors/brands/', searchParams)
}

export const updateAmbassadorFavorite = (payload: UpdateAmbassadorFavoriteRequest) =>
    api.patch<PartipostApiResponse<UpdateAmbassadorFavoriteResponse>>('/ambassadors/favorite/', payload)

export enum EngagementType {
    VIEWS = 'views',
    REACH = 'reach',
    LIKES = 'likes',
    COMMENTS = 'comments',
    SHARES = 'shares',
    IMPRESSION = 'impression',
}

interface ContentTypeValue {
    platform_content_type: PLATFORM_CONTENT_TYPE_ENUM
    engagement_type: EngagementType[]
}

export interface BrandContentType {
    uuid: string
    name: string
    value: ContentTypeValue[]
}

export interface GetBrandContentTypesRequest {
    brands: string
}

export const getBrandContentTypes = (params: GetBrandContentTypesRequest) => {
    const searchParams = new URLSearchParams()
    searchParams.set('brands', params.brands)
    return api.get<PartipostApiResponse<BrandContentType[]>>('/ambassadors/brands/content-type/', searchParams)
}

export interface AmbassadorFilter {
    key: string
    value?: string
    options: string[]
}

export interface FilterResponse {
    results: AmbassadorFilter[]
}

export interface GetAmbassadorFiltersRequest {
    brands: string
    content_type: string
}

export const getAmbassadorFilters = (params: GetAmbassadorFiltersRequest) => {
    const searchParams = new URLSearchParams()
    searchParams.set('brands', params.brands)
    searchParams.set('platform_content_type', params.content_type)
    return api.get<PartipostApiResponse<FilterResponse>>('/ambassadors/filters/', searchParams)
}

export interface AmbassadorBrandStats {
    uuid: string
    name: string
    task_completed_count: number
}

export interface AmbassadorEngagementStats {
    reach?: number
    view?: number
    impression?: number
    comments?: number
    engagement_rate?: number
    content_engagement_rate?: number
    engagement_sum?: number
    like?: number
    comment?: number
    share?: number
    save?: number
    tag?: number
    cta_click?: number
    link_click?: number
    sticker_tap?: number
    total_watch_time_second?: number
    average_watch_time_second?: number
    total_play_time_second?: number
    watched_full_video_percentage?: number
}

export interface AmbassadorResponse {
    uuid: string
    partiposter_profile: PartiposterProfile & {
        social_media_accounts: {
            follower_count: number | null
            username: string | null
            platform: PLATFORM_ENUM
        }
    }
    profile_tier: string
    brand: AmbassadorBrandStats
    engagement_stats: AmbassadorEngagementStats
    tags: ProfileToTagGrouped[]
}

export interface AmbassadorListResponse {
    results: AmbassadorResponse[]
}

export interface GetAmbassadorListRequest {
    brands: string
    content_type: PLATFORM_CONTENT_TYPE_ENUM
    ordering?: string
    offset?: number
    filteringParams?: {
        [key: string]: string[] | string | undefined
    }
}

export const getPlatformIdFromContentType = (contentType: PLATFORM_CONTENT_TYPE_ENUM): PLATFORM_ENUM | null => {
    if (PLATFORM_CONTENT[PLATFORM_ENUM.IG].includes(contentType)) {
        return PLATFORM_ENUM.IG
    }
    if (PLATFORM_CONTENT[PLATFORM_ENUM.TIKTOK].includes(contentType)) {
        return PLATFORM_ENUM.IG
    }
    if (PLATFORM_CONTENT[PLATFORM_ENUM.FB].includes(contentType)) {
        return PLATFORM_ENUM.IG
    }
    if (PLATFORM_CONTENT[PLATFORM_ENUM.TWITTER].includes(contentType)) {
        return PLATFORM_ENUM.IG
    }

    if (PLATFORM_CONTENT[PLATFORM_ENUM.YOUTUBE].includes(contentType)) {
        return PLATFORM_ENUM.IG
    }

    return null
}

export const getAmbassadorList = (params: GetAmbassadorListRequest) => {
    const { brands, content_type, filteringParams, ordering, offset } = params
    const searchParams = new URLSearchParams()
    searchParams.set('brands', brands)
    searchParams.set('platform_content_type', content_type)
    searchParams.set('limit', MAX_PAGE_SIZE.toString())
    searchParams.set('offset', offset ? offset.toString() : '0')
    searchParams.set('ordering', ordering ? ordering : '')

    if (filteringParams) {
        Object.keys(filteringParams).forEach(key => {
            const value = filteringParams[key]
            if (!value) return

            if (key === 'profile_tier') {
                const profileTier = typeof value === 'string' ? value.split(',') : value
                const platform = getPlatformIdFromContentType(content_type)
                const formattedProfileTier = profileTier.map(tier => `${platform}_${tier}`).join(',')
                searchParams.set(key, formattedProfileTier)
            } else if (Array.isArray(value)) {
                searchParams.set(key, value.join(','))
            } else {
                searchParams.set(key, value)
            }
        })
    }
    return api.get<PartipostApiResponse<AmbassadorListResponse>>('/ambassadors/', searchParams)
}
